import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { delay, finalize, map, switchMap } from 'rxjs/operators';
import { ComponentClassGroupDto } from '../../models/component-class-group/component-class-group-dto.model';
import { ResetStateDefaults } from '../base-action-class.model';
import { ComponentClassGroupRequestService } from '../../services/request/component-class-group-request.service';
import { ModuleAttributeGroupRequestService } from '../../services/request/module-attribute-group-request.service';
import { ComponentClassGroupDetailMenuEnum } from '../../enums/component-class-group/component-class-group-detail-menu.enum';
import { calcDefaultTimeoutOf } from '../../services/client/request-client.service';
import { CompoApp } from '../../services/CompoApp';
import { ClassTreeNode } from '../../models/frontend_entities/ClassTreeNode';
import { MapUtils } from '../../utils/MapUtils';
import { ClassTreeUtils } from '../../utils/ClassTreeUtils';
import { ClassUtils } from '../../utils/ClassUtils';
import { ComponentClassGroupDetailAction } from './component-class-group-detail.actions';
import { ModuleAttributeWithOptionDto } from '../../models/module_attribute/ModuleAttributeWithOptionDto';
import { ModuleAttributeGroupDto } from '../../models/module_attribute_group/ModuleAttributeGroupDto';

export interface ComponentClassGroupDetailStateModel {
  id: string | null;
  componentClassGroup: ComponentClassGroupDto | null;
  selectedMenu: ComponentClassGroupDetailMenuEnum;
  inProgress: boolean;
  initialized: boolean;

  // Classes state
  classTree: ClassTreeNode[];
  allClassIds: string[];
  allLeafClassIds: string[];
  initialSelection: string[];
  currentSelection: string[];
  currentSelectionOnlyLeafs: string[];
  disabledClassIds: string[];
  classesInProgress: boolean;
  classesLoaded: boolean;

  // Data field groups state
  moduleAttributes: ModuleAttributeWithOptionDto[];
  moduleAttributeGroups: ModuleAttributeGroupDto[];
  dataFieldGroupsInProgress: boolean;
  dataFieldGroupsLoaded: boolean;
}

const getDefaults = (): ComponentClassGroupDetailStateModel => ({
  id: null,
  componentClassGroup: null,
  selectedMenu: ComponentClassGroupDetailMenuEnum.DATA_FIELD_GROUP,
  inProgress: false,
  classTree: [],
  initialized: false,
  allClassIds: [],
  allLeafClassIds: [],
  initialSelection: [],
  currentSelection: [],
  currentSelectionOnlyLeafs: [],
  disabledClassIds: [],
  classesInProgress: false,
  classesLoaded: false,
  moduleAttributes: [],
  moduleAttributeGroups: [],
  dataFieldGroupsInProgress: false,
  dataFieldGroupsLoaded: false
});

@State<ComponentClassGroupDetailStateModel>({
  name: 'componentClassGroupDetail',
  defaults: getDefaults()
})
@Injectable()
export class ComponentClassGroupDetailXsState {

  constructor(
    private readonly componentClassGroupRequestService: ComponentClassGroupRequestService,
    private readonly moduleAttributeGroupRequestService: ModuleAttributeGroupRequestService,
    private readonly app: CompoApp
  ) {}

  //region SELECTORS
  @Selector()
  static id(state: ComponentClassGroupDetailStateModel): string | null {
    return state.id;
  }

  @Selector()
  static initialized(state: ComponentClassGroupDetailStateModel): boolean {
    return state.initialized;
  }

  @Selector()
  static componentClassGroup(state: ComponentClassGroupDetailStateModel): ComponentClassGroupDto | null {
    return state.componentClassGroup || null;
  }

  @Selector()
  static selectedMenu(state: ComponentClassGroupDetailStateModel): ComponentClassGroupDetailMenuEnum {
    return state.selectedMenu;
  }

  @Selector()
  static inProgress(state: ComponentClassGroupDetailStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static classTree(state: ComponentClassGroupDetailStateModel): ClassTreeNode[] {
    return state.classTree;
  }

  @Selector()
  static classSelection(state: ComponentClassGroupDetailStateModel): Set<string> {
    return new Set<string>(state.currentSelection || []);
  }

  @Selector()
  static classSelectionOnlyLeafs(state: ComponentClassGroupDetailStateModel): Set<string> {
    return new Set<string>(state.currentSelectionOnlyLeafs || []);
  }

  @Selector()
  static allClassIds(state: ComponentClassGroupDetailStateModel): string[] {
    return state.allClassIds;
  }

  @Selector()
  static allLeafClassIds(state: ComponentClassGroupDetailStateModel): string[] {
    return state.allLeafClassIds;
  }

  @Selector()
  static disabledClassIds(state: ComponentClassGroupDetailStateModel): Set<string> {
    return new Set<string>(state.disabledClassIds || []);
  }

  @Selector()
  static assignedClassCount(state: ComponentClassGroupDetailStateModel): number {
    if (state.classesLoaded && state.initialSelection) {
      return state.initialSelection.length;
    }
    return state.componentClassGroup?.assignedClassCount ?? 0;
  }

  @Selector()
  static classesInProgress(state: ComponentClassGroupDetailStateModel): boolean {
    return state.classesInProgress;
  }

  @Selector()
  static classesLoaded(state: ComponentClassGroupDetailStateModel): boolean {
    return state.classesLoaded;
  }

  @Selector()
  static classesSaveRequired(state: ComponentClassGroupDetailStateModel): boolean {
    return !ComponentClassGroupDetailXsState.areArraysEqualAsSets(state.currentSelection, state.initialSelection);
  }

  @Selector()
  static moduleAttributes(state: ComponentClassGroupDetailStateModel): ModuleAttributeWithOptionDto[] {
    return state.moduleAttributes || [];
  }

  @Selector()
  static moduleAttributeGroups(state: ComponentClassGroupDetailStateModel): ModuleAttributeGroupDto[] {
    return state.moduleAttributeGroups || [];
  }

  @Selector()
  static moduleAttributeGroupCount(state: ComponentClassGroupDetailStateModel): number {
    return state.moduleAttributeGroups?.length ?? 0;
  }

  @Selector()
  static dataFieldGroupsInProgress(state: ComponentClassGroupDetailStateModel): boolean {
    return state.dataFieldGroupsInProgress;
  }

  @Selector()
  static dataFieldGroupsLoaded(state: ComponentClassGroupDetailStateModel): boolean {
    return state.dataFieldGroupsLoaded;
  }

  private static areArraysEqualAsSets(arr1: string[] | null | undefined, arr2: string[] | null | undefined): boolean {
    if (!arr1 || !arr2) return arr1 === arr2;
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    return set1.size === set2.size && [...set1].every(x => set2.has(x));
  }
  //endregion

  //region ACTIONS
  @Action(ComponentClassGroupDetailAction.LoadComponentClassGroup)
  public loadComponentClassGroup(
    ctx: StateContext<ComponentClassGroupDetailStateModel>,
    action: ComponentClassGroupDetailAction.LoadComponentClassGroup
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Component class group request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('Component class group id must be set!'));
    }
    if (state.id === action.id && state.componentClassGroup) {
      return of(undefined);
    }

    ctx.patchState({
      id: action.id,
      componentClassGroup: null,
      inProgress: true
    });

    const startDate: Date = new Date();

    return forkJoin({
      componentClassGroup: this.componentClassGroupRequestService.getById(action.id),
      moduleAttributes: this.componentClassGroupRequestService.getModuleAttributes(action.id),
      moduleAttributeGroups: this.moduleAttributeGroupRequestService.getForComponentClassGroup(action.id)
    }).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      map(({ componentClassGroup, moduleAttributes, moduleAttributeGroups }) => {
        ctx.patchState({
          id: componentClassGroup.id,
          componentClassGroup,
          moduleAttributes: moduleAttributes || [],
          moduleAttributeGroups: moduleAttributeGroups || [],
          dataFieldGroupsLoaded: true,
          initialized: true
        });
      }),
      finalize(() => {
        ctx.patchState({
          inProgress: false
        });
      })
    );
  }

  @Action(ComponentClassGroupDetailAction.SelectMenu)
  public selectMenu(
    ctx: StateContext<ComponentClassGroupDetailStateModel>,
    action: ComponentClassGroupDetailAction.SelectMenu
  ):void {
    const state = ctx.getState();

    if (state.inProgress) {
      throw new Error('Component class group request is in progress!');
    }
    if (!action.menu) {
      throw new Error('Menu must be set!');
    }

    ctx.patchState({
      selectedMenu: action.menu
    });
  }

  @Action(ComponentClassGroupDetailAction.DeleteComponentClassGroup)
  public delete(
    ctx: StateContext<ComponentClassGroupDetailStateModel>,
    action: ComponentClassGroupDetailAction.DeleteComponentClassGroup
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Component class group request is in progress!'));
    }
    const id = action.id || state.id;
    if (!id) {
      return throwError(() => new Error('Component class group id must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const startDate = new Date();
    return this.componentClassGroupRequestService.delete(id).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      map(() => {
        ctx.patchState({
          id: null,
          componentClassGroup: null
        });
      }),
      finalize(() => {
        ctx.patchState({
          inProgress: false
        });
      })
    );
  }

  @Action(ComponentClassGroupDetailAction.UpdateName)
  public updateName(
    ctx: StateContext<ComponentClassGroupDetailStateModel>,
    action: ComponentClassGroupDetailAction.UpdateName
  ): Observable<void> {
    const state = ctx.getState();
    const id = state.id;
    if (!id) {
      return throwError(() => new Error('Component class group id must be set!'));
    }
    if (state.inProgress) {
      return throwError(() => new Error('Component class group request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const startDate: Date = new Date();
    return this.componentClassGroupRequestService.update(id, { name: action.name }).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      map((updatedGroup: ComponentClassGroupDto) => {
        ctx.patchState({
          componentClassGroup: {
            ...state.componentClassGroup,
            ...updatedGroup
          }
        });
      }),
      finalize(() => {
        ctx.patchState({
          inProgress: false
        });
      })
    );
  }

  @Action(ComponentClassGroupDetailAction.LoadClasses)
  public loadClasses(
    ctx: StateContext<ComponentClassGroupDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    if (!state.id) {
      return throwError(() => new Error('State is not initialized!'));
    }

    ctx.patchState({
      classesInProgress: true
    });

    const user = this.app.data.getUser();
    const orgId = user?.organization?.id;

    const start: Date = new Date();

    return forkJoin({
      compoClasses: from(this.app.classService.getClassTreeForOrg(orgId)),
      assignedClasses: this.componentClassGroupRequestService.getAssignedClasses(state.id),
      scopeAssignedClasses: this.componentClassGroupRequestService.getScopeAssignedClasses(state.id)
    }).pipe(
      delay(calcDefaultTimeoutOf(start)),
      map(({ compoClasses, assignedClasses, scopeAssignedClasses }) => {
        const allLeafClassIds = compoClasses
          .filter(c => c.canContainComponent)
          .map(c => c.id);
        const allLeafClassIdSet = new Set<string>(allLeafClassIds);

        const initialSelection = (assignedClasses || []).filter(id => allLeafClassIdSet.has(id));
        const currentSelection = [...initialSelection];
        const disabledClassIds = (scopeAssignedClasses || []).filter(id => allLeafClassIdSet.has(id));
        const disabledClassIdSet = new Set<string>(disabledClassIds);

        const classMap = MapUtils.arrayToMap(compoClasses, 'id');
        const classTreeNodes = ClassTreeUtils.buildTreeNodes(classMap, false, false, []);
        const rootChildren = (classTreeNodes[0]?.children || []).map(node => this.cloneAndMarkNode(node, disabledClassIdSet));

        const allClassIds = compoClasses
          .filter(cl => cl.id !== ClassUtils.INSTANCE_ROOT_CLASS_ID)
          .map(cl => cl.id);

        const currentSelectionOnlyLeafs = [...currentSelection];

        ctx.patchState({
          classTree: rootChildren,
          allClassIds,
          allLeafClassIds,
          initialSelection,
          currentSelection,
          currentSelectionOnlyLeafs,
          disabledClassIds,
          classesLoaded: true
        });
      }),
      finalize(() => {
        ctx.patchState({
          classesInProgress: false
        });
      })
    );
  }

  @Action(ComponentClassGroupDetailAction.ChangeClassSelection)
  public changeClassSelection(
    ctx: StateContext<ComponentClassGroupDetailStateModel>,
    action: ComponentClassGroupDetailAction.ChangeClassSelection
  ): Observable<void> {
    const state = ctx.getState();
    const rawIds = action.ids ? [...action.ids] : [];
    const leafIdSet = new Set<string>(state.allLeafClassIds || []);
    const validIds = Array.from(new Set(rawIds.filter(id => Boolean(id) && leafIdSet.has(id))));

    ctx.patchState({
      currentSelection: validIds,
      currentSelectionOnlyLeafs: validIds
    });
    return of(undefined);
  }

  @Action(ComponentClassGroupDetailAction.SaveClassSelection)
  public saveClassSelection(
    ctx: StateContext<ComponentClassGroupDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    const groupId = state.id;
    if (!groupId) {
      return throwError(() => new Error('Component class group id must be set!'));
    }
    if (state.classesInProgress) {
      return throwError(() => new Error('Save is already in progress!'));
    }

    ctx.patchState({
      classesInProgress: true
    });

    const startDate = new Date();
    const leafIdSet = new Set<string>(state.allLeafClassIds || []);
    const selection = (state.currentSelection || []).filter(id => Boolean(id) && leafIdSet.has(id));

    return this.componentClassGroupRequestService.saveAssignedClasses(groupId, selection).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      switchMap(() => this.componentClassGroupRequestService.getModuleAttributes(groupId)),
      map((moduleAttributes: ModuleAttributeWithOptionDto[]) => {
        ctx.patchState({
          initialSelection: selection,
          moduleAttributes: moduleAttributes || []
        });
      }),
      finalize(() => {
        ctx.patchState({
          classesInProgress: false
        });
      })
    );
  }

  @Action(ComponentClassGroupDetailAction.ResetClassSelection)
  public resetClassSelection(
    ctx: StateContext<ComponentClassGroupDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    const initialSelection = [...(state.initialSelection || [])];

    ctx.patchState({
      currentSelection: initialSelection,
      currentSelectionOnlyLeafs: initialSelection
    });
    return of(undefined);
  }

  @Action(ComponentClassGroupDetailAction.LoadDataFieldGroups)
  public loadDataFieldGroups(
    ctx: StateContext<ComponentClassGroupDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();
    const groupId = state.id;
    if (!groupId) {
      return throwError(() => new Error('State is not initialized!'));
    }

    ctx.patchState({
      dataFieldGroupsInProgress: true
    });

    const start: Date = new Date();

    return forkJoin({
      moduleAttributes: this.componentClassGroupRequestService.getModuleAttributes(groupId),
      moduleAttributeGroups: this.moduleAttributeGroupRequestService.getForComponentClassGroup(groupId)
    }).pipe(
      delay(calcDefaultTimeoutOf(start)),
      map(({ moduleAttributes, moduleAttributeGroups }) => {
        ctx.patchState({
          moduleAttributes: moduleAttributes || [],
          moduleAttributeGroups: moduleAttributeGroups || [],
          dataFieldGroupsLoaded: true
        });
      }),
      finalize(() => {
        ctx.patchState({
          dataFieldGroupsInProgress: false
        });
      })
    );
  }

  @Action(ComponentClassGroupDetailAction.SaveDataFieldGroups)
  public saveDataFieldGroups(
    ctx: StateContext<ComponentClassGroupDetailStateModel>,
    action: ComponentClassGroupDetailAction.SaveDataFieldGroups
  ): Observable<void> {
    const state = ctx.getState();
    const groupId = state.id;
    if (!groupId) {
      return throwError(() => new Error('Component class group id must be set!'));
    }

    ctx.patchState({
      dataFieldGroupsInProgress: true
    });

    const start = new Date();
    return this.moduleAttributeGroupRequestService.saveAll(action.dto).pipe(
      delay(calcDefaultTimeoutOf(start)),
      switchMap(() => this.moduleAttributeGroupRequestService.getForComponentClassGroup(groupId)),
      map((groups: ModuleAttributeGroupDto[]) => {
        ctx.patchState({
          moduleAttributeGroups: groups || []
        });
      }),
      finalize(() => {
        ctx.patchState({
          dataFieldGroupsInProgress: false
        });
      })
    );
  }

  @Action([ComponentClassGroupDetailAction.ResetState, ResetStateDefaults])
  public resetState(ctx: StateContext<ComponentClassGroupDetailStateModel>): Observable<void> {
    ctx.patchState({ ...getDefaults() });
    return of(undefined);
  }
  //endregion

  //region HELPERS
  private cloneAndMarkNode(node: ClassTreeNode, disabledIds: Set<string>): ClassTreeNode {
    const isLeaf = node.compoClass?.canContainComponent || !node.children || node.children.length === 0;
    const clonedChildren = (node.children || []).map(child => this.cloneAndMarkNode(child, disabledIds));

    let isNodeDisabled: boolean;
    if (isLeaf) {
      isNodeDisabled = node.compoClass?.id ? disabledIds.has(node.compoClass.id) : false;
    } else {
      isNodeDisabled = clonedChildren.length > 0 && clonedChildren.every(child => child.disabled);
    }

    return {
      ...node,
      disabled: isNodeDisabled,
      children: clonedChildren
    };
  }
  //endregion
}
